import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  useRef,
  useState,
} from "react";

import { Button, cn } from "@aircall/ds";
import { useTranslation } from "react-i18next";

import ThumbDown from "@/assets/images/customer_mood_thumb_down_grey.svg?react";
import ThumbDownActive from "@/assets/images/customer_mood_thumb_down_grey_active.svg?react";
import ThumbUp from "@/assets/images/customer_mood_thumb_up_grey.svg?react";
import ThumbUpActive from "@/assets/images/customer_mood_thumb_up_grey_active.svg?react";

type Rating = "customer_mood.agree" | "customer_mood.dont_agree";

export interface FeedbacksProps extends ComponentPropsWithoutRef<"div"> {
  onClickThumbDown: () => void;
  onClickThumbUp: () => void;
  onClickThumbUpUpdated?: <T>(previous: T) => void;
  onClickThumbDownUpdated?: <T>(previous: T) => void;
}

/**
 * Thumb-up / thumb-down rating control. Renders the neutral pair by default and
 * collapses to the chosen thumb + its label once rated; clicking the label
 * resets to the neutral pair (and replays the rating, reporting the previous one
 * to the `*Updated` callbacks when set).
 *
 * Every clickable affordance is a DS `Button variant="ghost"` so the control is
 * keyboard-focusable and gets the standard hover/focus treatment — the bare
 * branded SVGs are kept (no react-icons outline thumb exists) and ride inside
 * the buttons as `children`. `forwardRef` exposes the root `div`.
 */
export const Feedbacks = forwardRef<HTMLDivElement, Feedbacks.Props>(
  (
    componentProps: Feedbacks.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const {
      onClickThumbDown,
      onClickThumbDownUpdated,
      onClickThumbUp,
      onClickThumbUpUpdated,
      className,
      ...props
    } = componentProps;

    const { t } = useTranslation();

    const [feedback, setFeedback] = useState<Rating | null>(null);
    const previousRating = useRef<Rating | null>(null);

    const handleResetOnClick = () => {
      previousRating.current = feedback;
      setFeedback(null);
    };
    const handleThumbUpOnClick = () => {
      if (!previousRating.current || !onClickThumbUpUpdated) {
        onClickThumbUp();
      } else {
        onClickThumbUpUpdated(previousRating.current);
      }

      setFeedback("customer_mood.agree");
    };
    const handleThumbDownOnClick = () => {
      if (!previousRating.current || !onClickThumbDownUpdated) {
        onClickThumbDown();
      } else {
        onClickThumbDownUpdated(previousRating.current);
      }
      setFeedback("customer_mood.dont_agree");
    };

    if (feedback) {
      const isUp = feedback === "customer_mood.agree";
      const ActiveIcon = isUp ? ThumbUpActive : ThumbDownActive;

      return (
        <div
          ref={forwardRef}
          data-slot="feedbacks"
          className={className}
          {...props}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-test={
              isUp
                ? "customer-mood-thumb-up-active"
                : "customer-mood-thumb-down-active"
            }
            onClick={handleResetOnClick}
            className="h-auto gap-0.5 px-1 py-0.5 text-muted-foreground"
          >
            <ActiveIcon height={16} width={16} />
            <span className="text-xs font-medium">{t(feedback)}</span>
          </Button>
        </div>
      );
    }

    return (
      <div
        ref={forwardRef}
        data-slot="feedbacks"
        className={cn("flex gap-1", className)}
        {...props}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          data-test="customer-mood-thumb-up"
          onClick={handleThumbUpOnClick}
        >
          <ThumbUp height={16} width={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          data-test="customer-mood-thumb-down"
          onClick={handleThumbDownOnClick}
        >
          <ThumbDown height={16} width={16} />
        </Button>
      </div>
    );
  }
);
Feedbacks.displayName = "Feedbacks";

export namespace Feedbacks {
  export type Props = FeedbacksProps;
}
